import classNames from 'classnames'
import { TextField } from 'formik-mui'
import DeleteIcon from 'mdi-react/DeleteIcon'
import React, { useCallback, useMemo } from 'react'
import { CardFooter } from 'scenes/Account/components/Card'
import { NumberField } from 'scenes/Dashboard/components-next/Forms/Controllers'
import { SelectFormik } from 'scenes/Dashboard/components/Inputs/Select'
import {
  getPerformanceIndicatorTypes,
  isMonetaryType,
  isRangeType,
} from 'scenes/Dashboard/data/performanceIndicators/helpers'
import { PerformanceIndicatorType } from 'scenes/Dashboard/data/performanceIndicators/types'
import { makeStyles } from 'tss-react/mui'
import { connect, FastField, FormProps } from 'utils-next/forms'

import { Card, CardContent, IconButton, Theme } from '@mui/material'

import { OrganizationFormValues } from '../../OrganizationDetailsEdit.models'
import {
  buildNumericPerformanceIndicator,
  buildRangePerformanceIndicator,
} from './PerformanceIndicatorsEditList.component'
import {
  getOptionFromPerformanceIndicatorType,
  getPerformanceIndicatorTypeFromOption,
  PerformanceIndicatorFormValue,
} from './PerformanceIndicatorsEditList.models'

type PerformanceIndicatorEditOuterProps = {
  className: string
  index: number
  name: string
  performanceIndicatorValue: PerformanceIndicatorFormValue
  onClickDelete: () => void
}

type PerformanceIndicatorEditProps = PerformanceIndicatorEditOuterProps & {
  formik: FormProps<OrganizationFormValues>
}

const PerformanceIndicatorEdit = React.memo(
  ({
    className,
    index,
    name,
    performanceIndicatorValue,
    onClickDelete,
    formik: { isSubmitting, setFieldValue },
  }: PerformanceIndicatorEditProps) => {
    const { classes } = useStyles()
    const namePrefix = `${name}[${index}]`
    const { type, options } = usePerformanceIndicatorData(performanceIndicatorValue)
    const inputComponent = getPerformanceIndicatorInputComponent(type, namePrefix, isSubmitting)

    const onChangeType = useCallback(
      (newType: { value: PerformanceIndicatorType }) => {
        if (isRangeType(newType.value)) {
          setFieldValue(`${namePrefix}`, buildRangePerformanceIndicator(newType.value))
          setFieldValue(`${namePrefix}.value.min`, '')
          setFieldValue(`${namePrefix}.value.max`, '')
        } else {
          setFieldValue(`${namePrefix}`, buildNumericPerformanceIndicator(newType.value))
          setFieldValue(`${namePrefix}.value`, '')
        }
      },
      [namePrefix, setFieldValue]
    )

    return (
      <Card className={classNames(className, classes.container)}>
        <CardContent>
          <div className={classNames(classes.inputContainer, classes.optionTypeContainer)}>
            <SelectFormik
              options={options}
              label="Type"
              name={`${namePrefix}.type`}
              disabled={isSubmitting}
              className={classes.selectField}
              onChange={onChangeType}
              autoFocus
            />

            <FastField
              component={TextField}
              label="Measured on"
              name={`${namePrefix}.measuredOn`}
              type="date"
              disabled={isSubmitting}
              className={classes.dateField}
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <div className={classes.inputContainer}>{inputComponent}</div>
        </CardContent>
        <CardFooter className={classes.actions}>
          <IconButton aria-label="Delete Performance Indicator" onClick={onClickDelete}>
            <DeleteIcon />
          </IconButton>
        </CardFooter>
      </Card>
    )
  }
)

PerformanceIndicatorEdit.displayName = 'PerformanceIndicatorEdit'

function getPerformanceIndicatorTypeOptions() {
  return getPerformanceIndicatorTypes().map(getOptionFromPerformanceIndicatorType)
}

function usePerformanceIndicatorData(performanceIndicatorValue: PerformanceIndicatorFormValue) {
  const type = getPerformanceIndicatorTypeFromOption(performanceIndicatorValue.type)
  const options = useMemo(getPerformanceIndicatorTypeOptions, [])
  return { type, options }
}

function getPerformanceIndicatorInputComponent(
  type: PerformanceIndicatorType,
  namePrefix: string,
  isSubmitting: boolean
) {
  const isMonetary = isMonetaryType(type)
  const isRange = isRangeType(type)
  const adornmentText = isMonetaryType(type) ? 'USD' : undefined
  if (isRange) {
    return (
      <>
        <FastField
          component={NumberField}
          adornmentText={adornmentText}
          label="Min"
          name={`${namePrefix}.value.min`}
          disabled={isSubmitting}
          internalInputProps={{ decimalScale: 0 }}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <FastField
          component={NumberField}
          adornmentText={adornmentText}
          label="Max"
          name={`${namePrefix}.value.max`}
          disabled={isSubmitting}
          internalInputProps={{ decimalScale: 0 }}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </>
    )
  } else if (isMonetary) {
    return (
      <FastField
        component={NumberField}
        adornmentText={adornmentText}
        label="Numeric Value"
        name={`${namePrefix}.value`}
        disabled={isSubmitting}
        internalInputProps={{ decimalScale: 0 }}
        InputLabelProps={{ shrink: adornmentText }}
      />
    )
  } else {
    return (
      <FastField
        component={TextField}
        adornmentText={adornmentText}
        label="Text Value"
        name={`${namePrefix}.value`}
        disabled={isSubmitting}
        multiline
        InputLabelProps={{ shrink: adornmentText }}
      />
    )
  }
}

const useStyles = makeStyles()(({ spacing }: Theme) => ({
  container: {
    display: 'grid',
    gridGap: spacing(2),
  },
  inputContainer: {
    display: 'grid',
    gridGap: spacing(2),

    marginBottom: spacing(2),
    ['&:last-child']: {
      marginBottom: 0,
    },
  },
  optionTypeContainer: {
    gridTemplateColumns: 'auto auto',
  },
  selectField: {
    minWidth: spacing(41),
  },
  dateField: {
    minWidth: spacing(19),
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}))

export default connect<PerformanceIndicatorEditOuterProps, OrganizationFormValues>(PerformanceIndicatorEdit)
